import {
  ShoppingCart,
  Clock,
  CheckCircle,
  DollarSign,
  Activity,
  Users,
  Calendar,
  Search,
  Filter,
  ChevronRight,
} from "lucide-react";
import { useState } from "react";

const OverviewStats = ({ stats, orders, getStatusColor }) => {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredOrders = orders.filter((order) =>
    String(order.id).toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate total revenue from orders
  const totalRevenue =
    orders && Array.isArray(orders)
      ? orders.reduce(
          (sum, order) =>
            sum + (typeof order.totalPrice === "number" ? order.totalPrice : 0),
          0
        )
      : 0;

  return (
    <div className="flex flex-col xl:flex-row gap-6 lg:gap-8 animate-in slide-in-from-bottom-4 duration-500 min-h-screen">
      {/* Left Sidebar - Stats Panel */}
      <div className="xl:w-80 w-full xl:sticky xl:top-4 xl:self-start space-y-4 lg:space-y-6">
        {/* Stats Overview Card */}
        <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-6 text-white">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold">Dashboard</h2>
            <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
          </div>

          <div className="space-y-6">
            {[
              {
                icon: ShoppingCart,
                label: "Total Orders",
                value: stats.totalOrders,
                trend: "+12%",
                color: "text-blue-400",
              },
              {
                icon: Clock,
                label: "Active Orders",
                value: stats.pendingOrders,
                trend: "Live",
                color: "text-amber-400",
              },
              {
                icon: CheckCircle,
                label: "Completed",
                value: stats.completedOrders,
                trend: "+5%",
                color: "text-emerald-400",
              },
              {
                icon: DollarSign,
                label: "Revenue",
                value: `$${totalRevenue.toFixed(2)}`,
                trend: "+18%",
                color: "text-purple-400",
              },
            ].map((stat, index) => (
              <div
                key={stat.label}
                className="flex items-center justify-between py-3 border-b border-slate-700 last:border-b-0 animate-in slide-in-from-left-4 fill-mode-both"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <div className="flex items-center space-x-3">
                  <stat.icon className={`w-5 h-5 ${stat.color}`} />
                  <div>
                    <p className="font-semibold text-white">{stat.value}</p>
                    <p className="text-xs text-slate-400">{stat.label}</p>
                  </div>
                </div>
                <span className="text-xs font-medium text-slate-300 bg-slate-700 px-2 py-1 rounded">
                  {stat.trend}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="space-y-3">
            {[
              { label: "New Order", icon: ShoppingCart, color: "bg-blue-500" },
              {
                label: "View Reports",
                icon: Activity,
                color: "bg-emerald-500",
              },
              { label: "Manage Tables", icon: Users, color: "bg-purple-500" },
            ].map((action) => (
              <button
                key={action.label}
                className="w-full flex items-center space-x-3 p-3 rounded-xl hover:bg-gray-50 transition-colors duration-200"
              >
                <div className={`p-2 ${action.color} rounded-lg`}>
                  <action.icon className="w-4 h-4 text-white" />
                </div>
                <span className="font-medium text-gray-700">
                  {action.label}
                </span>
                <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content Area - Orders */}
      <div className="flex-1">
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
          {/* Header with Search and Filters */}
          <div className="px-6 py-5 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-4 sm:space-y-0">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Order Management
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Track and manage all orders
                </p>
              </div>

              <div className="flex items-center space-x-3">
                <div className="relative">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search by Order ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full sm:w-64"
                  />
                </div>
                <button className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors">
                  <Filter className="w-4 h-4" />
                  <span>Filter</span>
                </button>
              </div>
            </div>
          </div>

          {/* Orders Content */}
          <div className="p-4 lg:p-6">
            {filteredOrders.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <ShoppingCart className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {searchTerm ? "No matching orders found" : "No Active Orders"}
                </h3>
                <p className="text-gray-500 mb-6 max-w-sm mx-auto">
                  {searchTerm
                    ? `No orders found matching "${searchTerm}". Try a different search term.`
                    : "Your order management system is ready. New orders will appear here."}
                </p>
                {!searchTerm && (
                  <button className="bg-slate-900 text-white px-6 py-3 rounded-lg font-medium hover:bg-slate-800 transition-colors">
                    Create New Order
                  </button>
                )}
              </div>
            ) : (
              /* Responsive Kanban-Style Columns with Scroll */
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 h-full">
                {["pending", "preparing", "completed"].map((status) => (
                  <div key={status} className="flex flex-col h-full min-h-0">
                    {/* Column Header */}
                    <div className="flex items-center justify-between pb-3 mb-4 border-b border-gray-200 flex-shrink-0">
                      <h3 className="font-semibold text-gray-900 capitalize flex items-center space-x-2">
                        <div
                          className={`w-3 h-3 rounded-full ${
                            status === "pending"
                              ? "bg-amber-400"
                              : status === "preparing"
                              ? "bg-blue-400"
                              : "bg-emerald-400"
                          }`}
                        ></div>
                        <span className="text-sm lg:text-base">{status}</span>
                      </h3>
                      <span className="text-xs lg:text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        {
                          filteredOrders.filter(
                            (order) => order.status === status
                          ).length
                        }
                      </span>
                    </div>

                    {/* Scrollable Orders Container */}
                    <div
                      className="flex-1 overflow-y-auto pr-2 space-y-3"
                      style={{ maxHeight: "70vh" }}
                    >
                      {filteredOrders
                        .filter((order) => order.status === status)
                        .map((order, index) => (
                          <div
                            key={order.id}
                            className="bg-gray-50 border border-gray-200 rounded-xl p-3 lg:p-4 hover:shadow-md transition-shadow duration-200 animate-in slide-in-from-bottom-4 fill-mode-both cursor-pointer flex-shrink-0"
                            style={{ animationDelay: `${index * 100}ms` }}
                          >
                            <div className="flex items-start justify-between mb-3">
                              <div className="min-w-0 flex-1">
                                <h4 className="font-semibold text-gray-900 text-sm lg:text-base truncate">
                                  #{String(order.id).slice(-6)}
                                </h4>
                                <p className="text-xs lg:text-sm text-gray-600 flex items-center mt-1">
                                  <Users className="w-3 h-3 mr-1 flex-shrink-0" />
                                  <span className="truncate">
                                    {order.customerName}
                                  </span>
                                </p>
                              </div>
                              <span className="text-sm lg:text-lg font-bold text-gray-900 ml-2 flex-shrink-0">
                                {typeof order.totalPrice === "number"
                                  ? `$${order.totalPrice.toFixed(2)}`
                                  : "$0.00"}
                              </span>
                              {/* Show warning if items array is missing or empty */}
                              {(!order.items ||
                                !Array.isArray(order.items) ||
                                order.items.length === 0) && (
                                <div className="text-xs text-red-500 mt-1">
                                  No items data for this order
                                </div>
                              )}
                            </div>

                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2 text-xs lg:text-sm text-gray-600 min-w-0">
                                <Calendar className="w-3 h-3 flex-shrink-0" />
                                <span className="truncate">
                                  Table {order.tableNumber}
                                </span>
                              </div>
                              <div className="flex items-center space-x-1 text-xs text-gray-500 flex-shrink-0">
                                <Clock className="w-3 h-3" />
                                <span>15 min</span>
                              </div>
                            </div>
                          </div>
                        ))}

                      {/* Show message if no orders in this status */}
                      {filteredOrders.filter((order) => order.status === status)
                        .length === 0 && (
                        <div className="text-center py-8">
                          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3 mx-auto">
                            <ShoppingCart className="w-6 h-6 text-gray-400" />
                          </div>
                          <p className="text-sm text-gray-500">
                            No {status} orders
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
export default OverviewStats;
